/**
 * Offer Response Component
 * Allows students and agents to accept or reject university offers
 * with optional rejection reason
 */
import { useState, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Download, Share2, Loader2, 
  FileText, AlertTriangle, ThumbsUp, ThumbsDown, 
  MessageSquare, Clock, User, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import { applicationsAPI, downloadAuthenticatedFile } from '@/lib/api';

export const OfferResponsePanel = ({ 
  application, 
  userRole = 'student',
  onUpdate,
  offerDocuments = []
}) => {
  const [loading, setLoading] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [downloading, setDownloading] = useState(false);

  // Check if user can respond to offer
  const canRespond = application?.journey_stage === 'offer_issued' && !application?.offer_response;
  const hasResponded = !!application?.offer_response;
  const isAccepted = application?.offer_response === 'accept';
  const isRejected = application?.offer_response === 'reject';

  // Get offer letter documents
  const offerLetterDocs = offerDocuments.filter(doc => 
    doc.document_type === 'offer_letter' || 
    doc.document_type === 'conditional_offer' ||
    doc.document_type === 'unconditional_offer' ||
    doc.stage === 'offer_issued'
  );

  // Handle accept offer
  const handleAccept = async () => {
    setLoading(true);
    try {
      await applicationsAPI.respondToOffer(application.id, 'accept');
      toast.success('Congratulations! Offer accepted successfully');
      setShowAcceptDialog(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept offer');
    } finally {
      setLoading(false);
    }
  };

  // Handle reject offer
  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setLoading(true);
    try {
      await applicationsAPI.respondToOffer(application.id, 'reject', rejectionReason);
      toast.success('Offer rejected. Your feedback has been recorded.');
      setShowRejectDialog(false);
      setRejectionReason('');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject offer');
    } finally {
      setLoading(false);
    }
  };

  // Handle download offer letter
  const handleDownloadOffer = async (doc) => {
    setDownloading(true);
    try {
      await downloadAuthenticatedFile(
        applicationsAPI.downloadJourneyDocument(application.id, doc.id),
        doc.filename
      );
      toast.success('Offer letter downloaded');
    } catch (error) {
      const msg = error?.message || 'Failed to download offer letter';
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // If not at offer_issued stage, don't show anything
  if (application?.journey_stage !== 'offer_issued' && !hasResponded) {
    return null;
  }

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isAccepted ? 'bg-green-500/10 border-green-500/30' : 
      isRejected ? 'bg-red-500/10 border-red-500/30' : 
      'bg-teal-500/10 border-yellow-500/30'
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              isAccepted ? 'bg-green-100' : 
              isRejected ? 'bg-red-100' : 
              'bg-teal-100'
            }`}>
              {isAccepted ? (
                <ThumbsUp className="w-5 h-5 text-green-500" />
              ) : isRejected ? (
                <ThumbsDown className="w-5 h-5 text-red-500" />
              ) : (
                <FileText className="w-5 h-5 text-teal-600" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-gray-800">
                {isAccepted ? 'Offer Accepted' : isRejected ? 'Offer Rejected' : 'University Offer Received'}
              </h3>
              <p className="text-sm text-gray-500">
                {isAccepted ? 'Congratulations! You have accepted this offer.' :
                 isRejected ? 'You have declined this offer.' :
                 'Review and respond to your university offer'}
              </p>
            </div>
          </div>
          {hasResponded && (
            <Badge className={isAccepted ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}>
              {isAccepted ? 'Accepted' : 'Rejected'}
            </Badge>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Offer Letter Documents */}
        {offerLetterDocs.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-600">📄 Offer Letter Documents</p>
            <div className="space-y-2">
              {offerLetterDocs.map((doc) => (
                <div 
                  key={doc.id}
                  className="flex items-center justify-between bg-white rounded-lg p-3 border border-white/5"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-6 h-6 text-teal-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">{doc.filename}</p>
                      <p className="text-xs text-gray-500">{formatDate(doc.uploaded_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownloadOffer(doc)}
                      disabled={downloading}
                      className="border-yellow-500/50 text-teal-600 hover:bg-teal-500/10"
                    >
                      {downloading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      <span className="ml-2">Download</span>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Response Status */}
        {hasResponded && (
          <div className={`p-4 rounded-lg ${isAccepted ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
            <div className="flex items-start gap-3">
              {isAccepted ? (
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm text-gray-800">
                  {isAccepted ? 
                    'You have accepted this offer. The university will proceed with the next steps.' :
                    'You have rejected this offer.'}
                </p>
                {isRejected && application?.offer_rejection_reason && (
                  <div className="mt-2 p-2 bg-[#e9ecef]/50 rounded">
                    <p className="text-xs text-gray-500 mb-1">Rejection Reason:</p>
                    <p className="text-sm text-gray-600">{application.offer_rejection_reason}</p>
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-2">
                  <User className="w-3 h-3" /> {application?.offer_response_by}
                  <Clock className="w-3 h-3 ml-2" /> {formatDate(application?.offer_response_at)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {canRespond && (
          <div className="space-y-3">
            <div className="p-3 bg-teal-500/5 rounded-lg border border-yellow-500/20">
              <p className="text-sm text-teal-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                {userRole === 'agent' 
                  ? 'You can accept or reject this offer on behalf of your student.'
                  : 'Please review the offer carefully before responding.'}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => setShowAcceptDialog(true)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-gray-800"
                data-testid="accept-offer-btn"
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                Accept Offer
              </Button>
              <Button
                onClick={() => setShowRejectDialog(true)}
                variant="outline"
                className="flex-1 border-red-500/50 text-red-500 hover:bg-red-500/10"
                data-testid="reject-offer-btn"
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                Reject Offer
              </Button>
            </div>

            {userRole === 'agent' && offerLetterDocs.length > 0 && (
              <p className="text-xs text-gray-500 text-center">
                💡 Tip: Download and share the offer letter with your student before responding
              </p>
            )}
          </div>
        )}
      </div>

      {/* Accept Confirmation Dialog */}
      <AlertDialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <AlertDialogContent className="bg-[#f1f3f5] border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800 flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-green-500" />
              Accept University Offer
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Are you sure you want to accept this offer from <strong className="text-gray-800">{application?.university_name}</strong> for <strong className="text-gray-800">{application?.course_name}</strong>?
              <br /><br />
              This action will move your application to the next stage.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#e9ecef] border-gray-200 text-gray-800 hover:bg-gray-200">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAccept}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-gray-800"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Yes, Accept Offer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog with Reason */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-[#f1f3f5] border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <ThumbsDown className="w-5 h-5 text-red-500" />
              Reject University Offer
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              You are about to reject the offer from {application?.university_name}.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
              <p className="text-sm text-red-600">
                ⚠️ This action cannot be undone. Please provide a reason for rejection.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-600">Reason for Rejection *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please explain why you're rejecting this offer (e.g., found a better program, financial reasons, location preference, etc.)"
                className="bg-[#e9ecef] border-gray-200 text-gray-800 mt-2"
                rows={4}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRejectDialog(false);
                  setRejectionReason('');
                }}
                className="border-gray-300 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={loading || !rejectionReason.trim()}
                className="bg-red-600 hover:bg-red-700 text-gray-800"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                Reject Offer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfferResponsePanel;
